import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Segmented, Switch } from "@dl/web-core-js/ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Movement } from "../pages/sales/figures";
import {
  EMPTY_CELL,
  percent,
  ratio,
  withCommas,
  type Cell,
  type Comparison,
  type PeriodColumn,
  type Selection,
} from "../pages/sales/periods";
import { useSalesBasis } from "./basis";
import { PeriodStrip } from "./PeriodStrip";
import { TOTAL_CAD_ID, TOTAL_USD_ID, type GridRow } from "./windows";

/**
 * The sales grid: clients down, periods across, one cell per client per period.
 *
 * Shared by the sales analysis page and the dashboard's client board. The
 * page owns the selection state (through `useSalesWindows`) and the rows
 * (through `useSalesRows`); the grid owns how they look and every control that
 * changes them, in one toolbar row, left to right: which periods, compared
 * against what, on which basis, and whether only issued invoices count. The
 * basis used to live in the app header, a screen away from the numbers it
 * moved; everything that changes this grid is now on this grid.
 *
 * The two currency totals are the first two rows, pinned, and their unit totals
 * are the denominator of every client's share. They used to sit in a banner
 * above the grid, which duplicated the grid and could not follow it past three
 * columns; the grid scrolls, the banner could not.
 */
export function SalesGrid({
  rows,
  columns,
  loading,
  stateKey,
  selected,
  onToggle,
  onRemove,
  single,
  comparison,
  onComparison,
  onDrill,
}: {
  rows: GridRow[];
  columns: PeriodColumn[];
  loading: boolean;
  stateKey: string;
  selected: Selection[];
  onToggle: (selection: Selection) => void;
  onRemove: (key: string) => void;
  /** One period at a time — the dashboard board. */
  single?: boolean;
  comparison: Comparison;
  onComparison: (value: Comparison) => void;
  onDrill: (row: GridRow) => void;
}) {
  const { t } = useTranslation();
  const { basis, setBasis, scope, setScope, count, setCount } = useSalesBasis();

  const totals = useMemo(
    () => ({
      cad: rows.find((row) => row.id === TOTAL_CAD_ID),
      usd: rows.find((row) => row.id === TOTAL_USD_ID),
    }),
    [rows],
  );

  // Totals first, then clients by the first column's units, largest first.
  const ordered = useMemo(() => {
    const first = columns[0]?.current.key;
    const clients = rows
      .filter((row) => !row.synthetic)
      .sort((a, b) => (first ? (b.values[first]?.units ?? 0) - (a.values[first]?.units ?? 0) : 0));
    return [totals.cad, totals.usd].filter((row): row is GridRow => Boolean(row)).concat(clients);
  }, [rows, columns, totals]);

  const cols = useMemo(() => {
    const c = defineColumns<GridRow>();

    const base = [
      c.custom(
        "name",
        (row) => `${row.name} ${row.no ?? ""}`,
        (row) => (
          <span className="flex flex-col leading-tight">
            <span className={row.synthetic ? "font-semibold text-ink" : "font-medium text-ink"}>{row.name}</span>
            {!row.synthetic && <span className="text-[11px] text-ink-muted">{`${row.id} - ${row.no ?? ""}`}</span>}
          </span>
        ),
        { headerKey: "column.client", priority: 1 },
      ),
      c.text("currency", (row) => row.currency, { headerKey: "column.currency", filter: "select", priority: 2 }),
    ];

    const share = (row: GridRow, key: string): number | null => {
      if (row.synthetic) return null;
      const units = row.values[key]?.units ?? 0;
      const total = (row.currency === "USD" ? totals.usd : totals.cad)?.values[key]?.units ?? 0;
      return total === 0 ? 0 : units / total;
    };

    const periods = columns.map((column, index) =>
      c.custom(
        column.key,
        (row) => row.values[column.current.key]?.gross ?? 0,
        (row) => (
          <PeriodCell
            current={row.values[column.current.key] ?? EMPTY_CELL}
            previous={row.values[column.compare.key] ?? EMPTY_CELL}
            share={share(row, column.current.key)}
            currency={row.currency || "CAD"}
          />
        ),
        {
          header: `${column.current.label} vs ${column.compare.label}`,
          filter: "range",
          align: "right",
          priority: index === 0 ? 3 : undefined,
        },
      ),
    );

    return [...base, ...periods];
  }, [columns, totals]);

  return (
    <DataTable
      data={ordered}
      columns={cols}
      getRowId={(row) => String(row.id)}
      loading={loading}
      rowHeight={64}
      stateKey={stateKey}
      onRowClick={onDrill}
      toolbarExtra={
        <div className="flex flex-1 flex-wrap items-center gap-x-4 gap-y-2">
          <PeriodStrip selected={selected} onToggle={onToggle} onRemove={onRemove} {...(single ? { single } : {})} />
          <Segmented
            value={comparison}
            onChange={onComparison}
            options={[
              { value: "previous", label: t("analysis.basis.previous", "vs previous") },
              { value: "same-last-year", label: t("analysis.basis.last-year", "vs last year") },
            ]}
          />
          <Segmented
            value={basis}
            onChange={setBasis}
            options={[
              { value: "billed" as const, label: t("sales.basis.billed", "Billed") },
              { value: "net" as const, label: t("sales.basis.net", "Net") },
            ]}
          />
          <Segmented
            value={count}
            onChange={setCount}
            options={[
              { value: "products" as const, label: t("sales.count.products", "Products") },
              { value: "items" as const, label: t("sales.count.items", "Items") },
            ]}
          />
          <Switch
            className="ml-auto"
            checked={scope === "issued"}
            onChange={(on) => setScope(on ? "issued" : "all")}
            label={t("sales.scope.issued", "Issued only")}
          />
        </div>
      }
      emptyMessage={t("table.empty", "Nothing to show")}
    />
  );
}

/**
 * One cell, three lines, three movements: money; units with this row's share
 * of its currency's units; net margin. Colour lands on the movement only.
 * Painting the figures themselves would make every row shout, and the eye
 * needs one thing to scan down a column of two hundred clients.
 */
function PeriodCell({
  current,
  previous,
  share,
  currency,
}: {
  current: Cell;
  previous: Cell;
  share: number | null;
  currency: string;
}) {
  const { t } = useTranslation();
  return (
    <span className="grid grid-cols-[1fr_auto] items-baseline gap-x-3 gap-y-0.5 leading-tight">
      <span className="tabular-nums font-medium text-ink">{withCommas(current.gross)}</span>
      <Movement value={ratio(current.gross, previous.gross)} />

      <span className="tabular-nums text-[12px] text-ink-muted">
        {share !== null && (
          <span className="text-ink-faint">
            {`${t("analysis.share.of-currency", "{{share}} of {{currency}}", { share: `${Math.round(share * 100)}%`, currency })} · `}
          </span>
        )}
        {`${current.units} u`}
      </span>
      <Movement value={ratio(current.units, previous.units)} small />

      <span className="tabular-nums text-[11px] text-ink-faint">{`NCM ${percent(current.ncmPercent)}`}</span>
      <Movement value={(current.ncmPercent - previous.ncmPercent) * 100} unit="pt" small />
    </span>
  );
}
